/** The capability a client advertises to receive binary snapshots (Rule 3). */
export const SNAPSHOT_BIN1 = 'snapshotBin1';
/** First byte of a binary snapshot frame (room for other binary message kinds later). */
export const SNAPSHOT_BIN_KIND = 0x01;
const FLAG_FULL = 0x01;
/** The `anim` values the compact form carries, by index. Append only — an index is wire. */
export const SNAPSHOT_BIN_ANIMS = ['idle', 'move', 'dodge'];
/** Integers the codec carries compactly: |n| ≤ 2^50, so zigzag stays an exact double. */
const MAX_COMPACT = 2 ** 50;
const BIT_X = 1 << 0;
const BIT_Z = 1 << 1;
const BIT_VX = 1 << 2;
const BIT_VZ = 1 << 3;
const BIT_FACING = 1 << 4;
const BIT_ANIM = 1 << 5;
const BIT_DODGE_REMAINING = 1 << 6;
const BIT_DODGE_COOLDOWN = 1 << 7;
const BIT_HEALTH = 1 << 8;
const BIT_HEALTH_MAX = 1 << 9;
const BIT_JSON = 1 << 10;
const KNOWN_BITS = (1 << 11) - 1;
/** Thrown when a value cannot ride the binary form at all (the caller sends JSON instead). */
export class SnapshotBinError extends Error {
    constructor(message) {
        super(message);
        this.name = 'SnapshotBinError';
    }
}
// --- byte writer / reader --------------------------------------------------
class ByteWriter {
    buf;
    length = 0;
    /** `capacity`: an upper bound when the caller knows one (compose), so the buffer never grows. */
    constructor(capacity = 64) {
        this.buf = new Uint8Array(capacity);
    }
    room(n) {
        if (this.length + n <= this.buf.length)
            return;
        let size = this.buf.length * 2;
        while (size < this.length + n)
            size *= 2;
        const next = new Uint8Array(size);
        next.set(this.buf.subarray(0, this.length));
        this.buf = next;
    }
    u8(n) {
        this.room(1);
        this.buf[this.length++] = n;
    }
    /** Unsigned LEB128 over a non-negative safe integer (arithmetic past 2^31, so it stays exact). */
    uvarint(n) {
        this.room(8);
        const buf = this.buf;
        let v = n;
        if (v < 0x80000000) {
            // The common case (ids, ticks, cm, ms): 32-bit shifts.
            while (v > 0x7f) {
                buf[this.length++] = (v & 0x7f) | 0x80;
                v >>>= 7;
            }
            buf[this.length++] = v;
            return;
        }
        while (v >= 0x80) {
            buf[this.length++] = (v % 0x80) + 0x80;
            v = Math.floor(v / 0x80);
        }
        buf[this.length++] = v;
    }
    /** Zigzag (0, -1, 1, -2, … → 0, 1, 2, 3, …) then LEB128. */
    svarint(n) {
        this.uvarint(n >= 0 ? n * 2 : -n * 2 - 1);
    }
    bytes(b) {
        this.room(b.length);
        this.buf.set(b, this.length);
        this.length += b.length;
    }
    /** The written bytes, as a view (no copy). */
    finish() {
        return this.buf.subarray(0, this.length);
    }
}
class ByteReader {
    buf;
    pos = 0;
    constructor(buf) {
        this.buf = buf;
    }
    get done() {
        return this.pos >= this.buf.length;
    }
    u8() {
        if (this.pos >= this.buf.length)
            throw new SnapshotBinError('truncated snapshot');
        return this.buf[this.pos++];
    }
    uvarint() {
        let n = 0;
        let scale = 1;
        for (let i = 0; i < 8; i += 1) {
            const b = this.u8();
            n += (b & 0x7f) * scale;
            if (b < 0x80) {
                if (!Number.isSafeInteger(n))
                    throw new SnapshotBinError('varint past 2^53');
                return n;
            }
            scale *= 0x80;
        }
        throw new SnapshotBinError('varint too long');
    }
    svarint() {
        const z = this.uvarint();
        return z % 2 === 0 ? z / 2 : -(z + 1) / 2;
    }
    bytes(n) {
        if (n > this.buf.length - this.pos)
            throw new SnapshotBinError('truncated snapshot');
        const out = this.buf.subarray(this.pos, this.pos + n);
        this.pos += n;
        return out;
    }
}
const utf8Encoder = new TextEncoder();
const utf8Decoder = new TextDecoder('utf-8', { fatal: true });
// --- compact-value tests ---------------------------------------------------
/** The integer n with n / scale === v exactly (v as the JSON wire would carry it), or null. */
function scaled(v, scale) {
    if (typeof v !== 'number' || !Number.isFinite(v))
        return null;
    const n = Math.round(v * scale);
    if (!Number.isSafeInteger(n) || Math.abs(n) > MAX_COMPACT)
        return null;
    // `n / scale` is what the decoder yields; it must be the wire value itself. (-0 → 0 on both
    // paths: JSON writes -0 as 0.)
    return n / scale === v ? n : null;
}
function compactInt(v) {
    return typeof v === 'number' && Number.isSafeInteger(v) && Math.abs(v) <= MAX_COMPACT ? v : null;
}
function isWireId(v) {
    return typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;
}
const COMPACT_FIELDS = [
    { key: 'x', bit: BIT_X, pack: (v) => scaled(v, 100), unpack: (n) => n / 100, signed: true },
    { key: 'z', bit: BIT_Z, pack: (v) => scaled(v, 100), unpack: (n) => n / 100, signed: true },
    { key: 'vx', bit: BIT_VX, pack: (v) => scaled(v, 100), unpack: (n) => n / 100, signed: true },
    { key: 'vz', bit: BIT_VZ, pack: (v) => scaled(v, 100), unpack: (n) => n / 100, signed: true },
    {
        key: 'facing',
        bit: BIT_FACING,
        pack: (v) => scaled(v, 1000),
        unpack: (n) => n / 1000,
        signed: true,
    },
    {
        key: 'anim',
        bit: BIT_ANIM,
        pack: (v) => {
            const i = typeof v === 'string' ? SNAPSHOT_BIN_ANIMS.indexOf(v) : -1;
            return i >= 0 ? i : null;
        },
        unpack: (n) => {
            const a = SNAPSHOT_BIN_ANIMS[n];
            if (a === undefined)
                throw new SnapshotBinError(`unknown anim index ${n}`);
            return a;
        },
        signed: false,
    },
    {
        key: 'dodgeRemainingMs',
        bit: BIT_DODGE_REMAINING,
        pack: compactInt,
        unpack: (n) => n,
        signed: true,
    },
    {
        key: 'dodgeCooldownMs',
        bit: BIT_DODGE_COOLDOWN,
        pack: compactInt,
        unpack: (n) => n,
        signed: true,
    },
    { key: 'health', bit: BIT_HEALTH, pack: compactInt, unpack: (n) => n, signed: true },
    { key: 'healthMax', bit: BIT_HEALTH_MAX, pack: compactInt, unpack: (n) => n, signed: true },
];
// --- encode -----------------------------------------------------------------
function writeEntity(w, delta) {
    if (!isWireId(delta.id))
        throw new SnapshotBinError(`entity id ${String(delta.id)} is not a wire id`);
    const record = delta;
    let mask = 0;
    const packed = [];
    const handled = new Set(['id']);
    for (const f of COMPACT_FIELDS) {
        const v = record[f.key];
        if (v === undefined)
            continue;
        const n = f.pack(v);
        if (n === null)
            continue; // rides the JSON fragment, exact
        mask |= f.bit;
        packed.push(n);
        handled.add(f.key);
    }
    // Every other key (kind, name, appearance, held, anything added later) — and any compact
    // field whose value the compact form could not reproduce — as one JSON object. Keys whose
    // value is `undefined` are dropped exactly as JSON.stringify drops them.
    let rest = null;
    for (const key of Object.keys(record)) {
        if (handled.has(key) || record[key] === undefined)
            continue;
        rest ??= {};
        rest[key] = record[key];
    }
    const json = rest === null ? null : utf8Encoder.encode(JSON.stringify(rest));
    if (json !== null)
        mask |= BIT_JSON;
    w.uvarint(delta.id);
    w.uvarint(mask);
    let i = 0;
    for (const f of COMPACT_FIELDS) {
        if ((mask & f.bit) === 0)
            continue;
        const n = packed[i++];
        if (f.signed)
            w.svarint(n);
        else
            w.u8(n);
    }
    if (json !== null) {
        w.uvarint(json.length);
        w.bytes(json);
    }
}
/**
 * One entity's self-delimiting binary fragment — the unit a server encodes once per tick and
 * shares across viewers (D4). Throws SnapshotBinError when the id is not a wire id.
 */
export function encodeEntityDeltaBin(delta) {
    const w = new ByteWriter();
    writeEntity(w, delta);
    return w.finish();
}
function checkSeq(name, v) {
    if (!isWireId(v))
        throw new SnapshotBinError(`${name} ${String(v)} is not a non-negative integer`);
}
/**
 * Compose a binary snapshot from per-entity fragments (each from `encodeEntityDeltaBin`), in
 * order. The composition is a header, the fragments verbatim, and the removed list.
 */
export function composeSnapshotBin(tick, full, lastProcessedSeq, fragments, removed) {
    checkSeq('tick', tick);
    // Signed: the zone reports -1 until the viewer's first input is accounted for.
    if (compactInt(lastProcessedSeq) === null) {
        throw new SnapshotBinError(`lastProcessedSeq ${String(lastProcessedSeq)} is not an integer`);
    }
    // One allocation of an upper bound: 2 header bytes, three 8-byte varints, the fragments, and the
    // removed list (an 8-byte varint each, plus its count).
    let size = 2 + 8 * 4 + 8 * removed.length;
    for (const f of fragments)
        size += f.length;
    const w = new ByteWriter(size);
    w.u8(SNAPSHOT_BIN_KIND);
    w.u8(full ? FLAG_FULL : 0);
    w.uvarint(tick);
    w.svarint(lastProcessedSeq);
    w.uvarint(fragments.length);
    for (const f of fragments)
        w.bytes(f);
    w.uvarint(removed.length);
    for (const id of removed) {
        if (!isWireId(id))
            throw new SnapshotBinError(`removed id ${String(id)} is not a wire id`);
        w.uvarint(id);
    }
    return w.finish();
}
/** Encode a whole snapshot (the reference path; the server composes shared fragments instead). */
export function encodeSnapshotBin(tick, d) {
    if (typeof d.full !== 'boolean')
        throw new SnapshotBinError('full is not a boolean');
    return composeSnapshotBin(tick, d.full, d.lastProcessedSeq, d.entities.map((e) => encodeEntityDeltaBin(e)), d.removed);
}
// --- decode -----------------------------------------------------------------
function readEntity(r) {
    const id = r.uvarint();
    const mask = r.uvarint();
    if ((mask & ~KNOWN_BITS) !== 0 || mask > KNOWN_BITS) {
        throw new SnapshotBinError(`unknown entity field bits ${mask.toString(2)}`);
    }
    const out = { id };
    for (const f of COMPACT_FIELDS) {
        if ((mask & f.bit) === 0)
            continue;
        out[f.key] = f.unpack(f.signed ? r.svarint() : r.u8());
    }
    if ((mask & BIT_JSON) !== 0) {
        const len = r.uvarint();
        let rest;
        try {
            rest = JSON.parse(utf8Decoder.decode(r.bytes(len)));
        }
        catch (err) {
            if (err instanceof SnapshotBinError)
                throw err;
            throw new SnapshotBinError('bad entity JSON fragment');
        }
        if (typeof rest !== 'object' || rest === null || Array.isArray(rest)) {
            throw new SnapshotBinError('entity JSON fragment is not an object');
        }
        for (const [k, v] of Object.entries(rest)) {
            if (k === 'id' || k === '__proto__')
                continue;
            out[k] = v;
        }
    }
    return out;
}
/**
 * Decode a binary snapshot frame into the envelope a JSON client would have parsed. Throws
 * SnapshotBinError on any malformed, truncated or over-long input — never a partial snapshot.
 */
export function decodeSnapshotBin(bytes) {
    const r = new ByteReader(bytes);
    const kind = r.u8();
    if (kind !== SNAPSHOT_BIN_KIND)
        throw new SnapshotBinError(`not a snapshot frame (kind ${kind})`);
    const flags = r.u8();
    if ((flags & ~FLAG_FULL) !== 0)
        throw new SnapshotBinError(`unknown snapshot flags ${flags}`);
    const tick = r.uvarint();
    const lastProcessedSeq = r.svarint();
    const count = r.uvarint();
    // Every entity is at least two bytes (id + mask): a count past that is a lie, refuse it
    // before allocating.
    if (count * 2 > bytes.length)
        throw new SnapshotBinError('entity count exceeds the frame');
    const entities = [];
    for (let i = 0; i < count; i += 1)
        entities.push(readEntity(r));
    const removedCount = r.uvarint();
    if (removedCount > bytes.length)
        throw new SnapshotBinError('removed count exceeds the frame');
    const removed = [];
    for (let i = 0; i < removedCount; i += 1)
        removed.push(r.uvarint());
    if (!r.done)
        throw new SnapshotBinError('trailing bytes after the snapshot');
    return {
        t: 'snapshot',
        tick,
        d: { full: (flags & FLAG_FULL) !== 0, lastProcessedSeq, entities, removed },
    };
}
//# sourceMappingURL=snapshot-bin.js.map